import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { getTemplateSetsByProject, createTemplateSet, duplicateTemplateSet, deleteTemplateSet, type TemplateSet } from '@/lib/api';
import { FileText, Plus, Copy, Trash2, Edit, Calendar, Upload, Download } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/app/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { toast } from 'sonner';
import ImportTemplateCSVDialog from '@/app/components/ImportTemplateCSVDialog';
import { serializeTemplateCSV } from '@/lib/templateCsv';
import { downloadCSV } from '@/lib/csv';

export default function TemplateSetsView() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [templateSets, setTemplateSets] = useState<TemplateSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<TemplateSet | null>(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateLanguage, setNewTemplateLanguage] = useState('fr');
  const [duplicateName, setDuplicateName] = useState('');

  const loadTemplateSets = async () => {
    if (projectId) {
      try {
        setIsLoading(true);
        const data = await getTemplateSetsByProject(projectId);
        setTemplateSets(data);
      } catch (error) {
        console.error('Error loading template sets:', error);
        toast.error('Failed to load template sets');
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadTemplateSets();
  }, [projectId]);

  const handleCreateTemplateSet = async () => {
    if (!projectId || !newTemplateName.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      // Create default template content structure
      const defaultTemplateContent = {
        primary_grouping_field: 'region_nom',
        campaign_name_template: 'Campaign - {region_nom}',
        ad_group_name_template: 'AG - {nom_standard}',
        campaign_status: 'Enabled' as const,
        ad_group_status: 'Enabled' as const,
        headlines: Array.from({ length: 15 }, (_, i) => ({
          slot_number: i + 1,
          variants: [
            {
              variant_order: 1,
              template_string: '',
              enabled: true,
            },
          ],
        })),
        descriptions: Array.from({ length: 4 }, (_, i) => ({
          slot_number: i + 1,
          variants: [
            {
              variant_order: 1,
              template_string: '',
              enabled: true,
            },
          ],
        })),
        keywords: [],
        final_url_template: '',
        path1_template: '',
        path2_template: '',
        callouts: [],
        sitelinks: [],
        generate_accent_stripped_keywords: true,
      };

      const newTemplateSet = await createTemplateSet({
        project_id: projectId,
        name: newTemplateName,
        language_code: newTemplateLanguage,
        status: 'draft',
        generation_settings: {
          filters: [],
        },
        template_content: defaultTemplateContent,
      });

      await loadTemplateSets();
      setIsCreating(false);
      setNewTemplateName('');
      setNewTemplateLanguage('fr');
      toast.success('Template set created successfully');
      
      // Navigate to editor
      navigate(`/projects/${projectId}/templates/${newTemplateSet.id}`);
    } catch (error) {
      console.error('Error creating template set:', error);
      toast.error('Failed to create template set');
    }
  };

  const handleDuplicateTemplateSet = async () => {
    if (!duplicateSource || !duplicateName.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      const duplicated = await duplicateTemplateSet(duplicateSource.id, duplicateName);
      await loadTemplateSets();
      setIsDuplicating(false);
      setDuplicateSource(null);
      setDuplicateName('');
      toast.success('Template set duplicated successfully');
      
      // Navigate to editor
      navigate(`/projects/${projectId}/templates/${duplicated.id}`);
    } catch (error) {
      console.error('Error duplicating template set:', error);
      toast.error('Failed to duplicate template set');
    }
  };

  const handleDeleteTemplateSet = async (templateSetId: string) => {
    try {
      await deleteTemplateSet(templateSetId);
      await loadTemplateSets();
      toast.success('Template set deleted');
    } catch (error) {
      console.error('Error deleting template set:', error);
      toast.error('Failed to delete template set');
    }
  };

  const getCompletionBadge = (ts: TemplateSet) => {
    const c = ts.template_content;
    const missing: string[] = [];

    const headlinesOk =
      (c.headlines || []).length >= 15 &&
      (c.headlines || []).every((s) => s.variants.some((v) => v.enabled && v.template_string.trim() !== ''));
    const descriptionsOk =
      (c.descriptions || []).length >= 4 &&
      (c.descriptions || []).every((s) => s.variants.some((v) => v.enabled && v.template_string.trim() !== ''));
    const keywordsOk = (c.keywords || []).length > 0;

    if (!headlinesOk) missing.push('headlines');
    if (!descriptionsOk) missing.push('descriptions');
    if (!keywordsOk) missing.push('keywords');

    if (missing.length === 0) {
      return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">Complete</Badge>;
    }
    return (
      <Badge variant="outline" className="text-destructive border-destructive/40 whitespace-nowrap">
        Incomplete ({missing.join(', ')})
      </Badge>
    );
  };

  return (
    <div className="app-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <FileText className="size-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">Template Sets</h1>
              <p className="text-muted-foreground">
                {templateSets.length} {templateSets.length === 1 ? 'template set' : 'template sets'} in this project
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setIsImporting(true)}>
          <Upload className="size-4 mr-2" />
          Import CSV
        </Button>
        <Dialog open={isCreating} onOpenChange={setIsCreating}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="size-4 mr-2" />
              New Template Set
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Template Set</DialogTitle>
              <DialogDescription>
                Create a new template set for your campaign generation
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  placeholder="e.g., French Main Template"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateTemplateSet();
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select value={newTemplateLanguage} onValueChange={setNewTemplateLanguage}>
                  <SelectTrigger id="language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">French (fr)</SelectItem>
                    <SelectItem value="en">English (en)</SelectItem>
                    <SelectItem value="es">Spanish (es)</SelectItem>
                    <SelectItem value="de">German (de)</SelectItem>
                    <SelectItem value="it">Italian (it)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreating(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTemplateSet}>
                Create Template Set
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ImportTemplateCSVDialog
        projectId={projectId ?? ''}
        open={isImporting}
        onOpenChange={setIsImporting}
        existingTemplates={templateSets}
        onImported={() => loadTemplateSets()}
      />

      {isLoading ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="size-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Loading template sets...</h3>
            <p className="text-muted-foreground mb-4 text-center max-w-md">
              Please wait while we load the template sets for this project.
            </p>
          </CardContent>
        </Card>
      ) : templateSets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="size-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No template sets yet</h3>
            <p className="text-muted-foreground mb-4 text-center max-w-md">
              Create your first template set to define how your campaigns, ads, and keywords will be generated.
            </p>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="size-4 mr-2" />
              Create Template Set
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templateSets.map((templateSet) => (
            <Card key={templateSet.id} className="hover:shadow-lg transition-shadow card-hover">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle>{templateSet.name}</CardTitle>
                    <div className="mt-1">{getCompletionBadge(templateSet)}</div>
                    <CardDescription className="mt-2">
                      Language: {templateSet.language_code.toUpperCase()}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="size-4" />
                    <span>Created {new Date(templateSet.created_at).toLocaleDateString()}</span>
                  </div>
                  
                  {templateSet.duplicated_from_template_set_id && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Copy className="size-4" />
                      <span>Duplicated from another template</span>
                    </div>
                  )}

                  <div className="pt-3 border-t flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/projects/${projectId}/templates/${templateSet.id}`)}
                    >
                      <Edit className="size-4 mr-2" />
                      Edit
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      title="Export as CSV"
                      onClick={() => {
                        const csv = serializeTemplateCSV(templateSet);
                        const slug = templateSet.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'template';
                        downloadCSV(`${slug}.template.csv`, csv);
                      }}
                    >
                      <Download className="size-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDuplicateSource(templateSet);
                        setDuplicateName(`${templateSet.name} (Copy)`);
                        setIsDuplicating(true);
                      }}
                    >
                      <Copy className="size-4" />
                    </Button>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Template Set</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{templateSet.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteTemplateSet(templateSet.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Duplicate Dialog */}
      <Dialog open={isDuplicating} onOpenChange={setIsDuplicating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Template Set</DialogTitle>
            <DialogDescription>
              Create a copy of "{duplicateSource?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="duplicate-name">New Template Name</Label>
              <Input
                id="duplicate-name"
                placeholder="Enter new template name"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleDuplicateTemplateSet();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDuplicating(false)}>
              Cancel
            </Button>
            <Button onClick={handleDuplicateTemplateSet}>
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
